import { COPILOT_ID } from "@repo/app-types/entities/copilot";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import * as Sentry from "@sentry/react";
import type { UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import {
  invalidateMastraThreadAction,
  useCreateMastraThreadMutation,
  useLazyLatestGradingMastraThreadQuery,
  useLazyMastraThreadQuery,
} from "../../api/entities/mastraThread.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  useMastraChat,
  type MastraChat,
} from "../agents/mastra/useMastraChat.js";
import {
  copilotThreadMetadataSchema,
  type CannedExchange,
  type CopilotChat,
  type CopilotThreadMetadata,
} from "./copilotContext.js";
import { useCopilotPageContext } from "./copilotPageContext.js";

interface ThreadState {
  localThreadId: string;
  initialUserMessageText?: string;
  initialExchange?: CannedExchange;
  remote?: RemoteThreadState;
  isNotFound?: boolean;
}

interface RemoteThreadState {
  id: string;
  resourceId: string;
  /**
   * Known at selection time; empty until Mastra generates one. Required so a
   * new source of threads can't quietly leave it out and fall back to the
   * placeholder title.
   */
  title: string | undefined;
  metadata: CopilotThreadMetadata;
}

function initializeThreadState(): ThreadState {
  return {
    localThreadId: uuid(),
  };
}

/**
 * Threads are company-scoped; new ones carry no scope metadata. Grounding
 * comes from `pageContext` instead, sent with every message.
 */
const NEW_THREAD_METADATA: CopilotThreadMetadata = {};

// Mastra titles a thread from its first user message, but it does so in the
// background: the title lands a second or two *after* the response stream
// closes, so refreshing the thread the moment the turn ends still reads the
// empty title it was created with. Poll for it instead.
const THREAD_TITLE_POLL_INTERVAL = 1000; // ms
const THREAD_TITLE_POLL_ATTEMPTS = 10;

export interface UseCopilotChatOptions {
  /** The caller's Mastra resourceId; undefined when they don't get the agent. */
  resourceId: string | undefined;
  /** Work that must finish before a message is sent. */
  onBeforeSend?: () => Promise<unknown>;
  /**
   * The call whose transcript hosts the chat. It opens on the call's latest
   * thread, and threads started in it are tagged with the call.
   */
  gradingId?: GradingId;
}

export interface UseCopilotChatResult {
  /** Undefined when the caller doesn't get the agent. */
  copilotChat: CopilotChat | undefined;
  /** Loading while the call's latest thread is looked up, as for any history load. */
  mastraChat: MastraChat;
  /** Selects the thread with this ID, or marks it not found. */
  openThread: (threadId: string) => Promise<void>;
  /** Starts a new chat without moving focus into the composer. */
  openNewThread: () => void;
  /** Starts a new chat in place of a thread that could not be loaded. */
  forgetNotFoundThread: () => void;
}

/**
 * One Ask Alpharun chat: the selected thread, the composer's draft, and the
 * Mastra chat that sends into it. The main chat and a call transcript's chat
 * are separate instances.
 */
export function useCopilotChat(
  options: UseCopilotChatOptions,
): UseCopilotChatResult {
  const { resourceId, onBeforeSend, gradingId } = options;

  const [threadState, setThreadState] = useState<ThreadState>(
    initializeThreadState,
  );
  const [composerDraft, setComposerDraft] = useState("");
  const [composerFocusRequestId, setComposerFocusRequestId] = useState(0);

  // What the user is currently viewing, sent with each message so the agent can
  // ground its answer in the on-screen resource. Read at send time, so it
  // reflects the route the user is on when they hit send — not when the thread
  // was opened.
  const pageContext = useCopilotPageContext();

  const currentRemoteThreadId = threadState.remote?.id;
  const onThreadSelect = useCallback(
    (remoteThread: {
      id: string;
      resourceId: string;
      title: string | undefined;
      metadata: CopilotThreadMetadata;
    }) => {
      // Re-selecting the current thread (e.g. backing out of the panel's
      // history view) is a no-op, keeping the half-typed draft. The caller's
      // thread copy comes from the history list cache, which can lag the
      // thread's own state.
      if (remoteThread.id === currentRemoteThreadId) {
        return;
      }
      setComposerDraft("");
      setThreadState({ localThreadId: uuid(), remote: remoteThread });
    },
    [currentRemoteThreadId],
  );

  const onNewThreadSelect = useCallback(
    (options?: {
      initialUserMessageText?: string;
      initialExchange?: CannedExchange;
    }) => {
      // For unsaved threads, return to this thread when starting a new thread
      if (currentRemoteThreadId !== undefined) {
        setComposerDraft("");
      }
      setComposerFocusRequestId((previousId) => previousId + 1);
      setThreadState({
        localThreadId: uuid(),
        initialUserMessageText: options?.initialUserMessageText,
        initialExchange: options?.initialExchange,
      });
    },
    [currentRemoteThreadId],
  );

  const openNewThread = useCallback(() => {
    setComposerDraft("");
    setThreadState({ localThreadId: uuid() });
  }, []);

  // Only a `?research_thread_id=` link can leave a thread not found.
  const forgetNotFoundThread = useCallback(() => {
    setThreadState((prevThread) =>
      prevThread.isNotFound ? { localThreadId: uuid() } : prevThread,
    );
  }, []);

  const [fetchThread] = useLazyMastraThreadQuery();
  const openThread = useCallback(
    async (threadId: string) => {
      try {
        const thread = await fetchThread({
          agentId: COPILOT_ID,
          threadId,
        }).unwrap();
        setThreadState({
          localThreadId: uuid(),
          remote: {
            id: thread.id,
            resourceId: thread.resourceId,
            title: thread.title,
            metadata: copilotThreadMetadataSchema.parse(thread.metadata),
          },
        });
      } catch {
        setThreadState({ localThreadId: uuid(), isNotFound: true });
      }
    },
    [fetchThread],
  );

  const [isGradingThreadLoading, setIsGradingThreadLoading] = useState(
    gradingId !== undefined,
  );
  const [fetchLatestGradingThread] = useLazyLatestGradingMastraThreadQuery();
  useEffect(() => {
    if (!gradingId || !resourceId) {
      return undefined;
    }

    // Start each call on a blank chat while its latest one is looked up, so
    // the previous call's chat is never shown or sent into.
    let isCurrentCall = true;
    const localThreadId = uuid();
    setThreadState({ localThreadId });
    setComposerDraft("");
    setIsGradingThreadLoading(true);

    fetchLatestGradingThread({
      agentId: COPILOT_ID,
      resourceId,
      gradingId,
    })
      .unwrap()
      .then((thread) => {
        if (!thread || !isCurrentCall) {
          return;
        }
        // Only while the blank chat above is still untouched: a chat picked
        // from history, started, or sent into meanwhile stays.
        setThreadState((prevState) =>
          prevState.localThreadId === localThreadId && !prevState.remote
            ? {
                localThreadId: uuid(),
                remote: {
                  id: thread.id,
                  resourceId: thread.resourceId,
                  title: thread.title,
                  metadata: copilotThreadMetadataSchema.parse(thread.metadata),
                },
              }
            : prevState,
        );
      })
      // A failed lookup leaves the blank chat in place.
      .catch((error: unknown) => Sentry.captureException(error))
      .finally(() => {
        if (isCurrentCall) {
          setIsGradingThreadLoading(false);
        }
      });

    return () => {
      isCurrentCall = false;
    };
  }, [fetchLatestGradingThread, gradingId, resourceId]);

  const dispatch = useAppDispatch();
  const [createThread] = useCreateMastraThreadMutation();
  const createCopilotThread = useCallback(
    async (
      resourceId: string,
      metadata: CopilotThreadMetadata,
    ): Promise<RemoteThreadState> => {
      const newThread = await createThread({
        agentId: COPILOT_ID,
        resourceId,
        metadata,
      }).unwrap();

      return {
        id: newThread.id,
        resourceId: newThread.resourceId,
        title: newThread.title,
        metadata: copilotThreadMetadataSchema.parse(newThread.metadata),
      };
    },
    [createThread],
  );

  // Polling outlives the turn that started it, so it has to stop when the
  // chat goes away: a lazy query triggered after unmount registers a
  // subscription that nothing is left to release.
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Refetches the thread until Mastra's generated title shows up, so the chat
   * header and the history list pick it up without a page reload.
   */
  const refreshGeneratedThreadTitle = useCallback(
    async (threadId: string) => {
      for (let attempt = 0; attempt < THREAD_TITLE_POLL_ATTEMPTS; attempt++) {
        if (!isMountedRef.current) {
          return;
        }

        // `false` skips the cached value, so every attempt hits the server and
        // writes what it finds back into the thread's cache entry.
        const { data: thread, isError } = await fetchThread(
          { agentId: COPILOT_ID, threadId },
          false,
        );
        if (thread?.title || isError) {
          break;
        }

        await new Promise((resolve) =>
          setTimeout(resolve, THREAD_TITLE_POLL_INTERVAL),
        );
      }

      if (!isMountedRef.current) {
        return;
      }

      // The history list carries the title too, and has its own cache entry.
      dispatch(invalidateMastraThreadAction({ agentId: COPILOT_ID, threadId }));
    },
    [dispatch, fetchThread],
  );

  // Determine if the current thread is readonly (not owned by the authenticated user)
  const isOwnThread =
    !threadState.remote || threadState.remote.resourceId === resourceId;

  const mastraChat = useMastraChat({
    agentId: COPILOT_ID,
    localThreadId: threadState.localThreadId,
    // Only load history if we have a selected remote thread
    loadThreadId: threadState.remote?.id,
    onSend: async () => {
      if (!resourceId) {
        throw new Error("Cannot send message without a valid resource ID");
      }

      // Prevent sending messages in readonly mode
      if (!isOwnThread) {
        throw new Error("Cannot send messages to a shared thread");
      }

      await onBeforeSend?.();

      const localThreadId = threadState.localThreadId;
      let remoteThread = threadState.remote;
      let onFinish: (() => void) | undefined = undefined;

      // If we're in a new thread state, create the thread first
      if (!remoteThread) {
        // Create the new thread in Mastra. One started from a call's
        // transcript is tagged with the call, which reopens it there.
        const newRemoteThread = await createCopilotThread(
          resourceId,
          gradingId ? { gradingId } : NEW_THREAD_METADATA,
        );

        // Update our state to reflect our local thread now has a remote counterpart
        setThreadState((prevState) => {
          if (prevState.localThreadId === localThreadId) {
            return { ...prevState, remote: newRemoteThread };
          }

          return prevState;
        });

        // Send the message in the newly created thread
        remoteThread = newRemoteThread;
        onFinish = () => {
          // After the first turn, pick up the server-side generated title.
          // A failed refresh only costs the title until the next reload, so
          // report it rather than surfacing it to the user.
          refreshGeneratedThreadTitle(newRemoteThread.id).catch(
            (error: unknown) => Sentry.captureException(error),
          );
        };
      }

      return {
        thread: {
          resourceId,
          threadId: remoteThread.id,
        },
        requestContext: pageContext ? { pageContext } : {},
        onFinish,
      };
    },
  });

  const prevLocalThreadId = useRef<string | undefined>(undefined);
  const onThreadChange = useEffectEvent((localThreadId: string) => {
    // Make sure we do this only once per thread change
    if (prevLocalThreadId.current === localThreadId) {
      return;
    }
    prevLocalThreadId.current = localThreadId;

    // Send initial message if provided
    if (threadState.initialUserMessageText) {
      mastraChat.sendMessage(threadState.initialUserMessageText);
    }
    if (threadState.initialExchange) {
      mastraChat.appendMessages(
        buildExchangeMessages(threadState.initialExchange),
      );
    }
  });

  // Handle thread changes
  const localThreadId = threadState.localThreadId;
  useEffect(() => {
    onThreadChange(localThreadId);
  }, [localThreadId, onThreadChange]);

  const canThreadTakeMessage = isOwnThread && !threadState.isNotFound;
  const { sendMessage, appendMessages } = mastraChat;
  const onSendMessage = useCallback(
    (text: string) => {
      if (!canThreadTakeMessage) {
        onNewThreadSelect({ initialUserMessageText: text });
        return;
      }
      sendMessage(text);
    },
    [canThreadTakeMessage, onNewThreadSelect, sendMessage],
  );
  const onAppendExchange = useCallback(
    (exchange: CannedExchange) => {
      if (!canThreadTakeMessage) {
        onNewThreadSelect({ initialExchange: exchange });
        return;
      }
      appendMessages(buildExchangeMessages(exchange));
    },
    [canThreadTakeMessage, onNewThreadSelect, appendMessages],
  );
  const isCopilotBusy =
    mastraChat.isRunning || mastraChat.isLoading || isGradingThreadLoading;

  const copilotChat = useMemo((): CopilotChat | undefined => {
    if (!resourceId) {
      return undefined;
    }

    return {
      resourceId,
      localThreadId: threadState.localThreadId,
      threadId: threadState.remote?.id,
      threadTitle: threadState.remote?.title,
      threadResourceId: threadState.remote?.resourceId ?? resourceId,
      threadMetadata: threadState.remote?.metadata ?? NEW_THREAD_METADATA,
      isThreadNotFound: threadState.isNotFound ?? false,
      onThreadSelect,
      onNewThreadSelect,
      onSendMessage,
      onAppendExchange,
      isCopilotBusy,
      composerFocusRequestId,
      composerDraft,
      onComposerDraftChange: setComposerDraft,
    };
  }, [
    resourceId,
    threadState,
    onThreadSelect,
    onNewThreadSelect,
    onSendMessage,
    onAppendExchange,
    isCopilotBusy,
    composerFocusRequestId,
    composerDraft,
  ]);

  return {
    copilotChat,
    mastraChat: isGradingThreadLoading
      ? { ...mastraChat, isLoading: true }
      : mastraChat,
    openThread,
    openNewThread,
    forgetNotFoundThread,
  };
}

function buildExchangeMessages(exchange: CannedExchange): UIMessage[] {
  return [
    {
      id: uuid(),
      role: "user",
      parts: [{ type: "text", text: exchange.userText }],
    },
    {
      id: uuid(),
      role: "assistant",
      parts: [{ type: "text", text: exchange.replyText }],
    },
  ];
}
